export type AppEnv = string;

export type AppConfig = {
  env: AppEnv;
  supabase: { url: string; publishableKey: string };
  apiBaseUrl: string;
  mainBotUsername: string;
  errorCollectorUrl: string;
};

let runtimeConfigPromise: Promise<void> | null = null;

export async function ensureRuntimeConfigLoaded(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (window.APP_ENV && window.APP_CONFIG) return;
  if (runtimeConfigPromise) return runtimeConfigPromise;

  runtimeConfigPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-runtime-config="1"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load /config.js runtime config')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = '/config.js';
    script.async = false;
    script.dataset.runtimeConfig = '1';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load /config.js runtime config'));
    document.head.appendChild(script);
  }).then(() => {
    if (!window.APP_ENV || !window.APP_CONFIG) {
      throw new Error('Runtime config script loaded but APP_ENV/APP_CONFIG is missing');
    }
  });

  return runtimeConfigPromise;
}

export function detectEnv(): AppEnv {
  const explicitEnv = typeof window !== 'undefined' ? String((window as any).APP_ENV || '').trim() : '';
  if (explicitEnv) return explicitEnv;
  throw new Error('Missing window.APP_ENV runtime config');
}

export function getAppConfig(): { env: AppEnv; config: AppConfig } {
  const env = detectEnv();
  const runtimeConfig = (typeof window !== 'undefined' ? (window as any).APP_CONFIG : null) || null;

  if (!runtimeConfig) {
    throw new Error('Missing window.APP_CONFIG runtime config');
  }

  const config: AppConfig = {
    env,
    apiBaseUrl: String(runtimeConfig.apiBaseUrl || '').trim(),
    mainBotUsername: String(runtimeConfig.mainBotUsername || '').trim(),
    errorCollectorUrl: String(runtimeConfig.errorCollectorUrl || '').trim(),
    supabase: {
      url: String(runtimeConfig.supabase?.url || '').trim(),
      publishableKey: String(runtimeConfig.supabase?.publishableKey || '').trim(),
    },
  };

  if (!config.apiBaseUrl || !config.mainBotUsername || !config.errorCollectorUrl) {
    throw new Error('Incomplete window.APP_CONFIG runtime config');
  }
  if (!config.supabase.url || !config.supabase.publishableKey) {
    throw new Error('Incomplete Supabase runtime config');
  }

  return { env, config };
}
